import { getPresetById, getPresetWorkspacesByPresetId } from "@/db/presets"
import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.presets', supabase)

type Row = Tables<'presets'>
type WsRow = Tables<'preset_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const presetWorkspaces = await getPresetWorkspacesByPresetId(supabase, id)
	return presetWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setPresets
 * @returns
 */
export function makePresetChangeHandler(
	setPresets: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `preset-changes-${uuid}`,
		channelName: `preset-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'presets',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const preset = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setPresets(prev => {
					const exists = prev.some(p => p.id === preset.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling preset update:${printObject(preset)}`)
					return prev.map(p => p.id === preset.id ? preset : p)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setPresets
 * @returns
 */
export function makePresetWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setPresets: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `preset-ws-changes-${uuid}`,
		channelName: `preset-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'preset_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const presetWs = payload.new as WsRow
				const id = presetWs.preset_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const preset = await getPresetById(supabase, id)
					if (preset) {
						setPresets(prev => {
 							if (prev.some(p => p.id === preset.id)) return prev
							logger.info(`Handling preset Insert:${printObject(preset)}`)
 							return [...prev, preset]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const presetWs = payload.old as WsRow
				const id = presetWs.preset_id
				setPresets(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling preset Delete:${id}`)
					return prev.filter(p => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}
